import { useQueryClient } from "@tanstack/react-query";

import type { AgendaItem } from "@elation/api-client/src/stitch-web";
import { getListAgendaItemsQueryKey } from "@elation/api-client/src/stitch-web";

import type { CachedAgendaItemList } from "@src/features/agenda/domain";
import { useAppBusEvent } from "@src/features/sse";

import { getAgendaReorderMutationKey } from "./useAgendaReorder";

/**
 * Agenda-owned React Query cache sync for encounter agenda SSE events.
 * Mount from AgendaV2View for the encounter whose list is displayed.
 *
 * Order/snapshot events are ignored while a reorder mutation for this encounter
 * is in the mutation cache, so same-tick SSE cannot overwrite optimistic order
 * before React re-renders.
 */
export function useAgendaQueryCacheSync(encounterId: number | undefined) {
  const queryClient = useQueryClient();

  const matchesEncounter = (eventEncounterId: number): boolean =>
    encounterId != null && eventEncounterId === encounterId;

  const isLocalReorderPending = (eventEncounterId: number): boolean =>
    queryClient.isMutating({
      mutationKey: getAgendaReorderMutationKey(eventEncounterId),
    }) > 0;

  useAppBusEvent("encounter.snapshot", (payload) => {
    if (!matchesEncounter(payload.encounterId)) return;
    if (isLocalReorderPending(payload.encounterId)) return;

    queryClient.setQueryData<CachedAgendaItemList>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({ ...old, data: { ...old?.data, items: payload.items } }),
    );
  });

  useAppBusEvent("agenda_item.created", (payload) => {
    if (!matchesEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedAgendaItemList>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => {
        const items: AgendaItem[] = old?.data?.items ?? [];
        if (items.some((item) => item.id === payload.item.id)) return old;
        return {
          ...old,
          data: { ...old?.data, items: [...items, payload.item] },
        };
      },
    );
  });

  useAppBusEvent("agenda_item.updated", (payload) => {
    if (!matchesEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedAgendaItemList>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          ...old?.data,
          items: (old?.data?.items ?? []).map((item: AgendaItem) =>
            item.id === payload.item.id ? payload.item : item,
          ),
        },
      }),
    );
  });

  useAppBusEvent("agenda_item.deleted", (payload) => {
    if (!matchesEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedAgendaItemList>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({
        ...old,
        data: {
          ...old?.data,
          items: (old?.data?.items ?? []).filter(
            (item: AgendaItem) => item.id !== payload.itemId,
          ),
        },
      }),
    );
  });

  useAppBusEvent("agenda_items.reordered", (payload) => {
    if (!matchesEncounter(payload.encounterId)) return;
    if (isLocalReorderPending(payload.encounterId)) return;

    queryClient.setQueryData<CachedAgendaItemList>(
      getListAgendaItemsQueryKey(payload.encounterId),
      (old) => ({ ...old, data: { ...old?.data, items: payload.items } }),
    );
  });
}
